/**
 * vp-event-probe.js — Visual-state capture probe for Virtual Player + human testing.
 *
 * Listens for game-state events (TurnPump, FSM, clock, narration,
 * phase-banner, errors), computes an interstitial-aware identifier, and either:
 *   - calls Playwright's `window.__vpCapture({type, data})` when present
 *   - or renders a PNG via html2canvas and ships it to the GameServer, over the
 *     live game WebSocket (primary) or `POST /api/vp-capture` (local fallback).
 *
 * Transport [2026-07-27-002-QASTI, D1]: the relative POST is NOT reachable on
 * prod -- firebase.json rewrites /api/** to the gameAPI Cloud Function, whose
 * route table has no vp-capture branch, so every frame of every real human
 * session 404'd into a console.warn while the GameServer receiver sat there
 * complete and never contacted. Captures now ride the already-authenticated
 * game WebSocket (reusing GameServerConnection's handle -- never a second
 * socket), which also sidesteps the Cloud Functions request-size limit that a
 * full-page base64 PNG can exceed. HTTP remains the local-dev fallback and
 * needs the `/api/vp-capture` proxy entry in vite.config.js.
 *
 * Health / sampling (window.__VP_PROBE_STATE):
 *   attempted / rendered / delivered / acked / failed / dropped_busy /
 *   sampled_out / gcs_failed — `attempted` vs `acked` is the health signal, and
 *   the reason failures are no longer invisible. Sample rate via `?vp_sample=`,
 *   localStorage `VP_PROBE_SAMPLE`, or window.__VP_PROBE_SAMPLE (default 1.0);
 *   test accounts and the storyboard-spine events are never sampled out.
 *
 * Naming convention (client-side ONLY — backend audit_key is untouched):
 *   NN.PP         — phase-beat tick
 *   NN.PP.I#      — interstitial within an active phase (I# resets per NN.PP)
 *   NN.I#         — interstitial between phases (after last phase, before next turn)
 *
 * ⚠️ OPT-IN SINCE 2026-07-30. IT USED TO BE OPT-OUT, AND IT SHIPPED TO PROD.
 *
 * The old gate was three NEGATIVE checks (`vp=0`, `VP_PROBE_OFF`,
 * `__VP_PROBE_OFF`) and nothing positive, so the probe installed itself on
 * EVERY page load and painted its `capture: manual-HHMMSS` badge over the live
 * game. Real players on play.reavers.io saw a debug badge, on both HUD skews,
 * for as long as the probe has existed. It was reported from a phone
 * playthrough on 2026-07-30 -- not by any gate, because no gate was looking.
 *
 * A DEV INSTRUMENT MUST BE OFF UNLESS SOMEONE ASKED FOR IT. "Off in production"
 * expressed as a list of ways to turn it off is not a default -- it is a
 * default-on with extra steps, and every new entry point (a bookmark, the
 * Store link, a shared URL) arrives without any of them.
 *
 * Enable (any truthy turns the probe ON):
 *   window.__VP_INSTRUMENTATION_ENABLED === true   <- THE VP HARNESS
 *   window.location.search contains `vp=1`
 *   localStorage.getItem('VP_PROBE_ON') === '1'
 *   window.__VP_PROBE_ON === true
 *
 * ⚠️ `__VP_INSTRUMENTATION_ENABLED` IS LOAD-BEARING AND MUST STAY FIRST IN
 * THAT LIST. The Virtual Player harness sets exactly that flag, via
 * `page.add_init_script(_VP_INSTRUMENTATION_FLAG_SRC)` before any page script
 * runs (tests/virtual_player/browser.py) -- it does NOT pass `?vp=1`. The VP
 * persona harness is the release gate, so gating the probe on a NEW signal the
 * harness does not send would have turned every capture-dependent assertion
 * into a silent no-op: green suite, no screens, nothing to look at. The same
 * flag already gates `__vpBuildPromptSnapshot` and friends, so this reuses the
 * harness's existing "I am a VP run" signal rather than inventing a parallel one.
 *
 * The legacy kill switches still win if both are present, so an existing
 * `VP_PROBE_OFF=1` in someone's localStorage keeps working.
 */
(function () {
    'use strict';

    // --- Gate: OPT-IN, with the legacy kill switches still winning ---------
    try {
        if (typeof window === 'undefined') return;
        if (window.__VP_PROBE_INSTALLED) return;

        var search = (window.location && window.location.search) || '';

        // Kill switches first: an explicit OFF beats an explicit ON, so a
        // stale VP_PROBE_OFF in a tester's localStorage still silences it.
        if (window.__VP_PROBE_OFF === true) return;
        if (/(?:^|[?&])vp=0(?:&|$)/.test(search)) return;
        if (window.localStorage && window.localStorage.getItem('VP_PROBE_OFF') === '1') return;

        // Now the POSITIVE gate. Absent all of these, the probe does not exist.
        // __VP_INSTRUMENTATION_ENABLED FIRST: that is the flag the VP harness
        // actually sets (browser.py add_init_script), and the harness is the
        // release gate. Dropping it here silences every capture in every VP
        // run while the suite stays green.
        var wanted = (
            window.__VP_INSTRUMENTATION_ENABLED === true
            || window.__VP_PROBE_ON === true
            || /(?:^|[?&])vp=1(?:&|$)/.test(search)
            || (window.localStorage && window.localStorage.getItem('VP_PROBE_ON') === '1')
        );
        if (!wanted) return;
    } catch (_) {
        return;
    }
    window.__VP_PROBE_INSTALLED = true;

    // --- Human-mode label generation --------------------------------------
    function _hhmmss(d) {
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    }

    const _humanLabel = `manual-${_hhmmss(new Date())}`;
    window.__VP_PROBE_LABEL = _humanLabel;

    // --- Interstitial accounting -----------------------------------------
    // Keyed by the last observed phase-beat. Resets when NN.PP changes.
    const _phaseState = {
        turn: null,                 // number | null
        phase: null,                // number | null (0..N; N+1 means "between phases"
                                    //   inter-phase, using NN.I# naming)
        interstitialInPhase: 0,     // I# counter for NN.PP.I#
        interstitialInterPhase: 0,  // I# counter for NN.I# (between phases)
        sawFinalPhase: false,       // true after we've seen a phase-beat at highest phase
    };

    /**
     * Compute the audit-ish identifier for non-phase-beat events.
     * Returns {turn, phase, interstitial, kind} where:
     *   kind = 'phase'   (NN.PP, not used directly — phase_beat is separate)
     *   kind = 'active'  (NN.PP.I#, during an active phase)
     *   kind = 'inter'   (NN.I#, between phases)
     *   kind = 'pre'     (no phase yet — pre-spawn / early)
     */
    function _nextInterstitial() {
        // If we have no phase context yet, bump inter-phase counter.
        if (_phaseState.turn == null) {
            _phaseState.interstitialInterPhase += 1;
            return {
                turn: null,
                phase: null,
                interstitial: _phaseState.interstitialInterPhase,
                kind: 'pre',
            };
        }
        // If we're between phases (saw a "final" phase-beat and haven't moved on):
        if (_phaseState.sawFinalPhase) {
            _phaseState.interstitialInterPhase += 1;
            return {
                turn: _phaseState.turn,
                phase: null,
                interstitial: _phaseState.interstitialInterPhase,
                kind: 'inter',
            };
        }
        _phaseState.interstitialInPhase += 1;
        return {
            turn: _phaseState.turn,
            phase: _phaseState.phase,
            interstitial: _phaseState.interstitialInPhase,
            kind: 'active',
        };
    }

    /**
     * Called on every clock-frame phase-beat. Resets interstitial counters
     * when NN.PP changes. Returns {turn, phase, interstitial: 0, kind: 'phase'}.
     */
    function _onPhaseBeat(turnNumber, phaseNumber, totalPhases) {
        const changed =
            _phaseState.turn !== turnNumber ||
            _phaseState.phase !== phaseNumber;
        if (changed) {
            _phaseState.turn = turnNumber;
            _phaseState.phase = phaseNumber;
            _phaseState.interstitialInPhase = 0;
            _phaseState.sawFinalPhase = false;
            _phaseState.interstitialInterPhase = 0;
        }
        // If we're at (or past) the "last" phase, mark as potential inter-phase.
        if (totalPhases && phaseNumber >= totalPhases) {
            _phaseState.sawFinalPhase = true;
        }
        return {
            turn: turnNumber,
            phase: phaseNumber,
            interstitial: 0,
            kind: 'phase',
        };
    }

    // --- Dispatcher -------------------------------------------------------

    let _seq = 0;
    const _html2canvasPromise = (function () {
        // Dynamic import so the dep only loads when probe is enabled.
        return null; // lazy-loaded on first capture attempt (see _captureViaHttp)
    })();
    let _html2canvasLoader = null;

    function _loadHtml2Canvas() {
        if (!_html2canvasLoader) {
            _html2canvasLoader = import('html2canvas').then(
                (mod) => mod.default || mod,
                (err) => {
                    console.warn('[vp-probe] html2canvas unavailable:', err);
                    return null;
                }
            );
        }
        return _html2canvasLoader;
    }

    // --- Observable state [2026-07-27-002-QASTI] --------------------------
    // A probe that silently drops every frame used to be indistinguishable from
    // an idle one: the only failure report was a bare console.warn, invisible to
    // the page and to any VP scenario. These counters are the LOUD-FAILURE LAW
    // applied client-side -- `attempted` vs `acked` is the whole health signal.
    const _state = {
        label: _humanLabel,
        transport: null,        // 'ws' | 'http' | null (nothing delivered yet)
        attempted: 0,           // dispatches that passed the sampling gate
        rendered: 0,            // html2canvas frames produced
        sampled_out: 0,         // dropped BY DESIGN (sampling), not a failure
        delivered: 0,           // handed to a transport that accepted it
        acked: 0,               // server confirmed the write (WS path)
        failed: 0,              // frame LOST -- no payload, or every transport refused
        ws_errors: 0,           // WS refused but HTTP saved it: degraded, NOT lost
        dropped_busy: 0,        // capture queue was full -- frames genuinely lost
        png_skipped: 0,         // Tier 3 deliberately not taken -- BY DESIGN, not a loss
        png_failed: 0,          // Tier 3 wanted but the render failed; text still sent
        text_missing: 0,        // Tier 2 unavailable -- the tier that grades is absent
        reentrant_suppressed: 0, // nested dispatch blocked by the recursion guard
        gcs_failed: 0,          // server wrote locally but could not persist
        last_error: null,
        last_path: null,
        last_screens_md: null,
        last_gcs_error: null,
    };
    window.__VP_PROBE_STATE = _state;

    // Set once the page begins unloading; stamps `final` on the remaining
    // frames so the server flushes the survey bundle to GCS.
    let _sessionEnding = false;

    // A frame was LOST. `failed` must mean exactly that and nothing softer, so
    // that the ledger holds once everything settles:
    //     attempted === delivered + failed + dropped_busy
    // That identity is what makes "the probe is silently dropping every frame"
    // provable from the page instead of merely suspected.
    function _fail(reason, err) {
        _state.failed += 1;
        _state.last_error = `${reason}: ${err && err.message ? err.message : err}`;
        // console.warn, NOT console.error: the probe WRAPS console.error to
        // emit an 'error' capture, so reporting a capture failure through it
        // would recurse. (_dispatch also holds a re-entrancy guard.)
        console.warn(`[vp-probe] ${reason}:`, err);
    }

    // A transport DEGRADED but the frame survived via the fallback. Recorded so
    // WS trouble is visible, but deliberately NOT counted as `failed` -- calling
    // a delivered frame "failed" would break the ledger identity above and make
    // a healthy fallback look like data loss.
    function _note(reason, err) {
        _state.ws_errors += 1;
        _state.last_error = `${reason}: ${err && err.message ? err.message : err}`;
        console.warn(`[vp-probe] ${reason} (falling back to HTTP):`, err);
    }

    // --- Sampling ---------------------------------------------------------
    // PAX-scale concurrency cannot afford a full-page html2canvas per beat from
    // every player. Rate is read fresh each dispatch so it can be turned down
    // live from the console without a reload.
    //   ?vp_sample=0.1  ·  localStorage VP_PROBE_SAMPLE  ·  window.__VP_PROBE_SAMPLE
    function _sampleRate() {
        try {
            const q = window.location && window.location.search
                ? /(?:^|[?&])vp_sample=([0-9.]+)/.exec(window.location.search)
                : null;
            if (q) return Math.max(0, Math.min(1, parseFloat(q[1])));
            if (typeof window.__VP_PROBE_SAMPLE === 'number') {
                return Math.max(0, Math.min(1, window.__VP_PROBE_SAMPLE));
            }
            const ls = window.localStorage && window.localStorage.getItem('VP_PROBE_SAMPLE');
            if (ls != null && ls !== '') {
                return Math.max(0, Math.min(1, parseFloat(ls)));
            }
        } catch (_) { /* fall through to the default */ }
        return 1.0;  // default: unchanged behaviour, nothing is thinned
    }

    // --- Tier 2: the terminal TEXT snapshot -------------------------------
    // Taken on EVERY dispatch. This is the tier that actually GRADES: the
    // storyboard checks are pure text over rendered lines
    // (`^Exits:`/`^Present:` for the render contract; `[PHASE:NN]` + the
    // `i.N actor: <skill lifecycle>` interval lines for M4). Nothing in
    // storyboard_replay.py or channel_beat_invariants.py reads a pixel.
    //
    // Delegates to the ttl-emitter's buildSnapshot so there is exactly ONE DOM
    // walker in the client. A second implementation here would drift from that
    // one's channel classification and feed the grader a channel map the
    // terminal never rendered.
    function _terminalSnapshot() {
        try {
            const ttl = window.__TTL;
            if (!ttl || typeof ttl.buildSnapshot !== 'function') {
                // LOUD: without text there is nothing to grade. Countable, not
                // a console-only shrug.
                _state.text_missing += 1;
                _state.last_error = 'ttl-emitter buildSnapshot unavailable — no text tier';
                return null;
            }
            return ttl.buildSnapshot();
        } catch (err) {
            _state.text_missing += 1;
            _state.last_error = `terminal snapshot threw: ${err && err.message}`;
            return null;
        }
    }

    // --- ch2 reveal state: is the dice row FINISHED, or still animating? ---
    //
    // ⚠️ NO FRAME NAMED `ch2-end` EVER OBSERVED THE END OF A ch2 ROW.
    // [2026-08-25-018-ENGINE]
    //
    // `ch2-end` is dispatched from the pump's `playback_complete` -- the
    // NARRATION playback lifecycle. A ch2 dice row is not narration playback:
    // `Ch2Renderer.renderResolutionEvent` runs its own spin+reveal
    // choreography, ~3.9s long, on a timeline the pump knows nothing about.
    // So the frame named "end" is taken on an unrelated clock, and lands
    // INSIDE the reveal window as often as not. A row mid-reveal renders with
    // no total, no contest label, no Favor/Dissonance and no damage tail --
    // pixel-identical to a row that never filled in.
    //
    // That is a capture oracle asserting an end state it cannot see, and on
    // 2026-08-25 it manufactured a critical pax-blocker off three such frames
    // ("no row rendered complete"). Later frames in the same session show every
    // one of those rows complete. THE LOUD-FAILURE LAW, in the grading surface:
    // the instrument returned a plausible zero and nothing said "still going".
    //
    // Two things close it, and neither moves an existing frame:
    //   1. `ch2_rows_pending` rides EVERY dispatched frame, so no frame is ever
    //      again ambiguous about whether a row was mid-animation when the
    //      shutter opened. `-1` means the DOM could not be read -- an absence
    //      that says so, never a 0.
    //   2. `ch2_row_settled` / sub `dice-settled` is a NEW frame taken when the
    //      last pending row finishes. That is the frame that shows the finished
    //      mechanical row, and it is the one to grade a ch2 row from.
    //
    // ⚠️ THE SUB IS `dice-settled`, NOT `ch2-settled`, DELIBERATELY.
    // `storyboard_replay._frame_channel` classifies any sub matching `^ch(\d+)`
    // as that channel's frame, so a `ch2-`-prefixed name would inject a second
    // channel-2 frame into every channel-correlation rule (C7 and friends) and
    // silently change verdicts this change has no business touching. A
    // non-channel name leaves every existing grader reading exactly what it
    // read before.
    //
    // The `ch2-end` frame itself is UNCHANGED -- same trigger, same moment,
    // same sequence position. Deferring it would have reordered it past the
    // ch0 that C7 requires to follow it.
    const CH2_SETTLE_POLL_MS = 120;
    const CH2_SETTLE_CEILING_MS = 12000;
    let _ch2Watching = false;

    function _ch2RowsPending() {
        try {
            const root = (typeof document !== 'undefined')
                ? (document.getElementById('history') || document)
                : null;
            if (!root || typeof root.querySelectorAll !== 'function') return -1;
            return root.querySelectorAll('.ch2-row:not([data-ch2-settled])').length;
        } catch (_err) {
            return -1;
        }
    }

    // Watch the pending set down to zero and take the settled frame. Bounded:
    // at the ceiling it dispatches ANYWAY, carrying the residual count, because
    // "rows never settled" is the single most interesting thing this instrument
    // can find and it must not be expressed as silence.
    function _watchCh2Settle() {
        if (_ch2Watching) return;
        _ch2Watching = true;
        const started = Date.now();
        (function poll() {
            const pending = _ch2RowsPending();
            const expired = (Date.now() - started) >= CH2_SETTLE_CEILING_MS;
            if (pending === 0 || pending < 0 || expired) {
                _ch2Watching = false;
                _dispatch('ch2_row_settled', 'dice-settled', {
                    ch2_settle_timed_out: !!(expired && pending > 0),
                    ch2_settle_waited_ms: Date.now() - started,
                });
                return;
            }
            setTimeout(poll, CH2_SETTLE_POLL_MS);
        })();
    }

    // --- Tier 3: the PNG, now SPARSE and UX-only --------------------------
    // The probe used to rasterize the full page through html2canvas on EVERY
    // dispatch -- ~20+ times per turn in phase-beat combat, on the PLAYER'S
    // device -- and then discard it, because no grader consumes images. Worse,
    // the rasterization was slow enough that the capture queue filled and real
    // frames were dropped (measured: 20 combat dispatches -> 8 delivered).
    //
    // A picture is now taken only where a HUMAN would want to look at one.
    // Trigger set is a product decision, deliberately small:
    //   1. errors (console + gandalf)
    //   2. world join
    //   3. character select
    //   4. first room render
    // Uniform for everyone -- there is no test-account or VP full-rate path.
    // There is no manual trigger and no periodic sample.
    const _PNG_EVENT_TYPES = new Set(['error', 'world_join', 'character_select']);
    let _sawFirstRoomRender = false;

    function _shouldRasterize(eventType, subEvent) {
        if (_PNG_EVENT_TYPES.has(eventType)) return true;
        // The FIRST completed anchor narration is the first room render. Later
        // ch0 completions are ordinary gameplay and stay text-only.
        if (subEvent === 'ch0-end' && !_sawFirstRoomRender) {
            _sawFirstRoomRender = true;
            return true;
        }
        return false;
    }

    // Test accounts are ALWAYS captured in full, whatever the rate is. These are
    // the sessions the storyboard survey grades; sampling them would quietly
    // strip evidence out of the exact runs we test with. (This governs the
    // EVENT + TEXT tiers only -- PNG sparsity is uniform for everyone.)
    const _TEST_ACCOUNT_RE = /(^vp\d*@)|(^test\d*@)|(^dev@)|(@test\.local$)|(@reavers\.local$)/i;
    function _isTestAccount() {
        try {
            const auth = window.UnifiedAuthService;
            const email = auth && auth.state && auth.state.currentUser
                ? auth.state.currentUser.email
                : null;
            return !!(email && _TEST_ACCOUNT_RE.test(email));
        } catch (_) {
            return false;
        }
    }

    // The storyboard spine. These frames define turn/phase structure and
    // failures; dropping them makes screens.md ungradeable no matter how many
    // narration frames survive. Sampling only ever thins the in-between frames.
    const _ALWAYS_CAPTURE = new Set([
        'spawn_ready', 'phase_beat', 'command_submitted', 'error',
        // The frame that shows a FINISHED dice row. Sampling it out would put
        // the grader back on `ch2-end`, which cannot see the end of a ch2 row
        // at all -- the exact blindness this event exists to remove.
        'ch2_row_settled',
        // The onboarding moments: one-shot per session, and the frames a human
        // reviewer reaches for first when a join goes wrong.
        'world_join', 'character_select',
    ]);

    function _shouldCapture(eventType) {
        if (_ALWAYS_CAPTURE.has(eventType)) return true;
        if (_isTestAccount()) return true;
        const rate = _sampleRate();
        if (rate >= 1) return true;
        if (rate <= 0) return false;
        return Math.random() < rate;
    }

    // --- Transport --------------------------------------------------------
    // D1 (DECIDED): WebSocket primary, HTTP fallback.
    //
    // The relative POST below NEVER reached the GameServer on prod --
    // firebase.json rewrites /api/** to the gameAPI Cloud Function, whose route
    // table has no vp-capture branch, so every frame of every real session 404'd
    // into a console.warn. The receiver was complete the whole time; the ADDRESS
    // was wrong. The WS is the authenticated socket the game already holds (we
    // reuse GameServerConnection's handle -- never open a second one), which also
    // sidesteps the Cloud Functions request-size limit a full-page PNG can hit.
    async function _deliverCapture(payload, wantPng) {
        // Tier 2 FIRST and unconditionally: cheap, and it is the payload that
        // grades. Taken before any await so it reflects the DOM at dispatch
        // time, not after a rasterization delay.
        const terminal = payload.__terminal || null;
        delete payload.__terminal;

        let pngBase64 = null;
        if (wantPng) {
            // Tier 3. Reached only for the sparse UX triggers -- the gate is in
            // _dispatch, BEFORE this call, so a non-triggering frame never pays
            // the html2canvas cost at all.
            const html2canvas = await _loadHtml2Canvas();
            if (!html2canvas) {
                // Not fatal: the text tier still grades. Countable, not silent.
                _state.png_failed += 1;
                _state.last_error = 'html2canvas not loaded — frame sent text-only';
            } else {
                try {
                    const canvas = await html2canvas(document.body, {
                        backgroundColor: '#000000',
                        logging: false,
                        useCORS: true,
                        scale: 1,
                    });
                    // toDataURL returns "data:image/png;base64,<...>" — strip prefix.
                    const dataUrl = canvas.toDataURL('image/png');
                    const commaIdx = dataUrl.indexOf(',');
                    pngBase64 = commaIdx >= 0 ? dataUrl.substring(commaIdx + 1) : dataUrl;
                    _state.rendered += 1;
                } catch (err) {
                    // A failed screenshot must NOT discard the graded text tier.
                    _state.png_failed += 1;
                    _state.last_error = `html2canvas render failed: ${err && err.message}`;
                }
            }
        } else {
            _state.png_skipped += 1;
        }

        if (!pngBase64 && !terminal) {
            // Nothing to send. The server refuses an empty capture, so refusing
            // here keeps the counters honest rather than booking a failure the
            // server would have to reject.
            _fail('nothing to capture', 'no png and no terminal snapshot');
            return;
        }

        const frame = {
            label: window.__VP_PROBE_LABEL,
            png_base64: pngBase64 || undefined,
            terminal: terminal || undefined,
            // Once the page starts unloading, every remaining frame is stamped
            // final so the server flushes the index + jsonl to GCS. A human
            // session has no teardown hook; this is the closest thing to one.
            final: _sessionEnding || undefined,
            ...payload,
        };

        // 1) WebSocket (primary).
        try {
            const conn = window.GameServerConnection;
            if (conn && typeof conn.sendVpCaptureOverWs === 'function') {
                if (conn.sendVpCaptureOverWs(frame)) {
                    _state.delivered += 1;
                    _state.transport = 'ws';
                    return;  // ack (or its absence) is counted in _recordVpCaptureAck
                }
            }
        } catch (err) {
            _note('vp-capture WS send failed', err);
            // fall through to HTTP rather than losing the frame
        }

        // 2) HTTP (local-dev fallback; needs the vite /api/vp-capture proxy).
        try {
            const resp = await fetch('/api/vp-capture', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(frame),
            });
            if (!resp.ok) {
                // THE original silent failure: a 404 from the Cloud Function /
                // emulator used to land in the bare catch below and vanish.
                _fail('vp-capture POST rejected', `HTTP ${resp.status}`);
                return;
            }
            _state.delivered += 1;
            _state.acked += 1;
            _state.transport = 'http';
            try {
                const body = await resp.json();
                if (body && body.path) _state.last_path = body.path;
                if (body && body.screens_md) _state.last_screens_md = body.screens_md;
                if (body && body.gcs_error) {
                    _state.gcs_failed += 1;
                    _state.last_gcs_error = body.gcs_error;
                }
            } catch (_) { /* body is optional */ }
        } catch (err) {
            _fail('vp-capture POST failed', err);
        }
    }

    // Re-entrancy guard. _wireHooks wraps console.error to emit an 'error'
    // capture; any console.error raised while a capture is in flight would
    // recurse without this. (_fail deliberately uses console.warn for the same
    // reason -- belt and braces, because the recursion is unbounded.)
    let _inDispatch = false;

    // Serialization of the capture -> transport pipeline (see _dispatch).
    //
    // The bound exists to cap memory when captures cannot drain as fast as they
    // arrive, which was a real risk when EVERY frame rasterized the full page: a
    // 20-dispatch combat burst enqueued 20 slow renders and the bound of 8 threw
    // 12 real frames away. Now that Tier 3 is sparse, the common frame is a cheap
    // DOM read and drains almost immediately, so the ceiling can be far higher
    // while still refusing to grow without limit.
    const _MAX_CAPTURE_QUEUE = 64;
    let _captureChain = Promise.resolve();
    let _queued = 0;

    function _dispatch(eventType, subEvent, extra) {
        if (_inDispatch) {
            // Re-entrant dispatch, i.e. something inside a capture called
            // console.error (which this probe wraps). Suppressing it is the
            // point of the guard, but it is still recorded -- a silent `return`
            // is how a probe hides its own blind spot. NOT part of the
            // attempted ledger: this frame never became a capture attempt.
            _state.reentrant_suppressed += 1;
            return;
        }

        // Sampling gate BEFORE the seq bump: a sampled-out frame must not burn
        // a sequence number, or screens.md shows gaps that read as dropped
        // frames to the survey.
        if (!_shouldCapture(eventType)) {
            _state.sampled_out += 1;
            return;
        }

        _seq += 1;
        _state.attempted += 1;

        // Every frame declares whether a ch2 row was mid-reveal when the
        // shutter opened. Read BEFORE the payload is built so it describes the
        // same instant the pixels will. See the block above `_ch2RowsPending`.
        const ch2Pending = _ch2RowsPending();
        const frameData = (extra && typeof extra === 'object' && !Array.isArray(extra))
            ? extra
            : {};
        frameData.ch2_rows_pending = ch2Pending;

        const slot =
            eventType === 'phase_beat'
                ? _onPhaseBeat(
                      (extra && extra.turn_number) || 0,
                      (extra && extra.phase_number) || 0,
                      (extra && extra.total_phases) || 0,
                  )
                : _nextInterstitial();

        const payload = {
            seq: _seq,
            ts_iso: new Date().toISOString(),
            event_type: eventType,
            sub_event: subEvent || null,
            turn: slot.turn,
            phase: slot.phase,
            interstitial: slot.interstitial,
            audit_key: (extra && extra.audit_key) || null,
            data: frameData,
            // Tier 2, attached at dispatch time (see _deliverCapture, which
            // moves it onto the frame). Carried under a private key so it does
            // not leak into the Playwright payload shape below.
            __terminal: _terminalSnapshot(),
        };

        // A row is mid-reveal RIGHT NOW, so a frame that actually shows it
        // finished is owed. Kicked from here rather than from the ch2 branch of
        // `_observePumpEvent` because a row can still be animating under frames
        // that have nothing to do with ch2 -- which is precisely how the empty
        // rows were read as final in the first place. Idempotent: a watch
        // already running is not restarted.
        if (ch2Pending > 0) _watchCh2Settle();

        // Playwright path wins when available.
        if (typeof window.__vpCapture === 'function') {
            _inDispatch = true;
            try {
                // The Playwright recorder owns its own row shape; do not leak
                // the private text-tier key into it.
                delete payload.__terminal;
                window.__vpCapture(payload);
                _state.delivered += 1;
                _state.acked += 1;
                _state.transport = 'playwright';
            } catch (err) {
                _fail('__vpCapture threw', err);
            } finally {
                _inDispatch = false;
            }
            return;
        }
        // Human-browser path: html2canvas -> WS (primary) / POST (fallback).
        //
        // SERIALIZED, not concurrent: two html2canvas passes over the same DOM
        // at once are expensive and can interleave mid-render. Frames that
        // arrive during a capture are QUEUED, not dropped -- a phase_beat lost
        // to a 200ms render window is a hole in the storyboard. The queue is
        // bounded; hitting the bound is a real loss, so it is counted, not
        // silently swallowed.
        if (_queued >= _MAX_CAPTURE_QUEUE) {
            _state.dropped_busy += 1;
            _state.last_error = 'capture queue full — frames are being lost';
            return;
        }
        // Tier-3 decision made HERE, before anything is queued or rendered, so
        // a non-triggering frame never touches html2canvas.
        const wantPng = _shouldRasterize(eventType, payload.sub_event);

        _queued += 1;
        _captureChain = _captureChain.then(() => {
            _inDispatch = true;
            return Promise.resolve(_deliverCapture(payload, wantPng))
                .catch((err) => _fail('capture delivery threw', err))
                .finally(() => {
                    _inDispatch = false;
                    _queued -= 1;
                });
        });
    }

    // --- Overlay badge (human-mode) ---------------------------------------
    function _renderBadge() {
        try {
            if (typeof window.__vpCapture === 'function') return; // Playwright mode — no badge
            const badge = document.createElement('div');
            badge.id = 'vp-probe-badge';
            badge.textContent = `capture: ${window.__VP_PROBE_LABEL}`;
            badge.style.cssText = [
                'position:fixed',
                'bottom:8px',
                'right:8px',
                'padding:4px 8px',
                'font:11px/1.2 monospace',
                'color:#ffa020',
                'background:rgba(0,0,0,0.75)',
                'border:1px solid #ffa020',
                'border-radius:4px',
                'z-index:9999',
                'pointer-events:auto',
                'cursor:pointer',
                'user-select:none',
            ].join(';');
            badge.title = 'Click to start a new capture session';
            badge.addEventListener('click', () => {
                const now = new Date();
                window.__VP_PROBE_LABEL = `manual-${_hhmmss(now)}`;
                _seq = 0;
                // A new capture session gets fresh counters, or the health
                // signal (attempted vs acked) reads across two unrelated runs.
                _state.label = window.__VP_PROBE_LABEL;
                _state.attempted = 0;
                _state.rendered = 0;
                _state.sampled_out = 0;
                _state.delivered = 0;
                _state.acked = 0;
                _state.failed = 0;
                _state.ws_errors = 0;
                _state.dropped_busy = 0;
                _state.png_skipped = 0;
                _state.png_failed = 0;
                _state.text_missing = 0;
                _state.reentrant_suppressed = 0;
                _sawFirstRoomRender = false;
                _state.gcs_failed = 0;
                _state.last_error = null;
                _phaseState.turn = null;
                _phaseState.phase = null;
                _phaseState.interstitialInPhase = 0;
                _phaseState.interstitialInterPhase = 0;
                _phaseState.sawFinalPhase = false;
                badge.textContent = `capture: ${window.__VP_PROBE_LABEL}`;
            });
            const attach = () => {
                if (document.body) document.body.appendChild(badge);
                else document.addEventListener('DOMContentLoaded', () =>
                    document.body && document.body.appendChild(badge),
                );
            };
            attach();
        } catch (err) {
            // Badge is best-effort.
        }
    }

    // --- Event subscriptions ---------------------------------------------
    function _wireHooks() {
        // FSM transition -> spawn_ready when reaching GAMEPLAY for the first time.
        let _sawSpawnReady = false;
        if (typeof window.FSMEvents !== 'undefined' && window.FSMEvents.on) {
            try {
                window.FSMEvents.on('terminal:gameplay', () => {
                    if (_sawSpawnReady) return;
                    _sawSpawnReady = true;
                    _dispatch('spawn_ready', 'pre', {});
                });
            } catch (_) { /* ignore */ }
        }

        // Poll for TerminalFSM as a fallback if FSMEvents is absent.
        let _fsmPollTimer = null;
        if (typeof window.TerminalFSM !== 'undefined') {
            _fsmPollTimer = setInterval(() => {
                try {
                    if (_sawSpawnReady) {
                        clearInterval(_fsmPollTimer);
                        return;
                    }
                    if (window.TerminalFSM.mode === 'GAMEPLAY') {
                        _sawSpawnReady = true;
                        _dispatch('spawn_ready', 'pre', {});
                        clearInterval(_fsmPollTimer);
                    }
                } catch (_) { /* ignore */ }
            }, 250);
        }

        // Patch TurnPump.event to observe turn_accepted + clock frames + narration.
        try {
            if (typeof window.TurnPump !== 'undefined' &&
                typeof window.TurnPump.event === 'function' &&
                !window.TurnPump.__vpWrapped) {
                const _origEvent = window.TurnPump.event.bind(window.TurnPump);
                window.TurnPump.event = function (name, data) {
                    try {
                        _observePumpEvent(name, data || {});
                    } catch (err) {
                        console.warn('[vp-probe] TurnPump observer threw:', err);
                    }
                    return _origEvent(name, data);
                };
                window.TurnPump.__vpWrapped = true;
            }
        } catch (_) { /* ignore */ }

        // Patch PhaseBanner.play to observe phase_banner enter.
        try {
            if (typeof window.PhaseBanner !== 'undefined' &&
                typeof window.PhaseBanner.play === 'function' &&
                !window.PhaseBanner.__vpWrapped) {
                const _origPlay = window.PhaseBanner.play.bind(window.PhaseBanner);
                window.PhaseBanner.play = function (phaseNumber, opts) {
                    _dispatch('phase_banner', 'phase-banner', {
                        phase_number: phaseNumber,
                        mode: (opts && opts.mode) || 'normal',
                    });
                    return _origPlay(phaseNumber, opts);
                };
                window.PhaseBanner.__vpWrapped = true;
            }
        } catch (_) { /* ignore */ }

        // Observe user-command submissions. processInput() in main.js runs BEFORE
        // GameServerConnection.processInput(); we hook the WebSocket sendDirectMessage
        // instead to catch the action as it leaves the client.
        try {
            if (typeof window.GameServerConnection !== 'undefined' &&
                typeof window.GameServerConnection.sendDirectMessage === 'function' &&
                !window.GameServerConnection.__vpWrappedSend) {
                const _origSend = window.GameServerConnection.sendDirectMessage.bind(
                    window.GameServerConnection,
                );
                window.GameServerConnection.sendDirectMessage = function (message) {
                    try {
                        if (message && message.type === 'action' && message.action) {
                            // Auto-look is server-dispatched — skip (tagged via TurnPump event).
                            const slug = _slugCommand(message.action);
                            _dispatch('command_submitted', `command_${slug}`, {
                                action: message.action,
                                target_name: message.target_name || null,
                            });
                        }
                    } catch (_) { /* ignore */ }
                    return _origSend(message);
                };
                window.GameServerConnection.__vpWrappedSend = true;
            }
        } catch (_) { /* ignore */ }

        // Console errors -> error event.
        try {
            const _origError = console.error;
            console.error = function () {
                try {
                    const msg = Array.from(arguments).map(String).join(' ');
                    _dispatch('error', 'error-console', { message: msg.substring(0, 200) });
                } catch (_) { /* ignore */ }
                return _origError.apply(console, arguments);
            };
        } catch (_) { /* ignore */ }
    }

    // --- Narration chapter tracking --------------------------------------
    // We track the current chapter typewriter start / mid / end. The typewriter
    // runs inside GameServerConnection, pacing at (audio_duration * 900) / length.
    // Mid fires at audio_duration * 0.5s after narration_received.
    let _narrationIdx = -1;
    let _midTimer = null;
    let _activeChapter = null;

    function _observePumpEvent(name, data) {
        switch (name) {
            case 'turn_accepted':
                _dispatch('turn_accepted', 'turn-accepted', { action: data.action });
                break;
            case 'frame_received': {
                if (!data || data.kind !== 'clock') break;
                const p = data.payload || {};
                _dispatch('phase_beat', 'phase-beat', {
                    turn_number: p.turn_number || 0,
                    phase_number: p.phase_number || 0,
                    total_phases: p.total_phases || 0,
                    audit_key: (data.coordinate && data.coordinate.audit_key) || null,
                    beat_mode: p.beat_mode || null,
                    is_boundary: !!p.is_boundary,
                });
                break;
            }
            case 'narration_received': {
                _narrationIdx += 1;
                const chapter = data.channel != null ? data.channel : 0;
                _activeChapter = chapter;
                _dispatch('narration_start', `ch${chapter}-start`, {
                    channel: chapter,
                    narration_idx: _narrationIdx,
                    // [combat-validation-observability 3b] Carry the cycle_id so
                    // the screens.md Audit column populates for narration frames
                    // (mirrors phase_beat). Name-space, NOT a UUID.
                    audit_key: data.cycle_id || data.audit_key || null,
                });
                // Schedule mid at 50% of audio_duration.
                if (_midTimer) clearTimeout(_midTimer);
                let audioDuration = 0;
                try {
                    // THE RECEIVED DURATION (`frame.md` P9) -- mirrors
                    // `_playNarration`, which no longer invents one.
                    //
                    // ⚠️ THIS PROBE COMPUTED `Math.max(3, text.length / 12)`
                    // ITSELF. An instrument that reproduces the invention it is
                    // measuring cannot see the invention's error: the VP's
                    // narration_mid would have fired at 50% of a made-up
                    // length, up to 29% wrong, and reported it as an
                    // audio-relative position. It now reads the same fact the
                    // renderer does, and falls back to a FLAT default when the
                    // frame carries none -- never to a text-length estimate,
                    // which would silently reinstate the thing being retired.
                    audioDuration = (
                        data.audioUrl && typeof data.audioDurationMs === 'number'
                            && data.audioDurationMs > 0
                            ? data.audioDurationMs / 1000
                            : 0
                    );
                } catch (_) { /* ignore */ }
                const midMs = audioDuration > 0 ? audioDuration * 500 : 2000;
                const idxSnapshot = _narrationIdx;
                const chapSnapshot = chapter;
                const cycleSnapshot = data.cycle_id || data.audit_key || null;
                _midTimer = setTimeout(() => {
                    _midTimer = null;
                    _dispatch('narration_mid', `ch${chapSnapshot}-mid`, {
                        channel: chapSnapshot,
                        narration_idx: idxSnapshot,
                        // [combat-validation-observability 3b] cycle_id snapshot.
                        audit_key: cycleSnapshot,
                    });
                }, midMs);
                break;
            }
            case 'playback_complete': {
                const chapter = data.channel != null ? data.channel : _activeChapter || 0;
                // [voice-drop-observability] The probe's narration timing is
                // TEXT-LENGTH-derived (assumed audio duration) — it can't see a
                // real voice drop. Attach the ACTUAL audio outcome from the voice
                // ring buffer so /survey sees actual-vs-assumed: whether the
                // frame's voice actually played, failed, was suppressed, or was
                // absent. Best-effort read; timing model is unchanged.
                const endCycle = data.cycle_id || data.audit_key || null;
                let audioOutcome = null;
                try {
                    const ring = (typeof window !== 'undefined') ? window.__VOICE_OUTCOMES : null;
                    if (ring && ring.length) {
                        // Prefer the newest outcome matching this cycle; else the
                        // newest for this channel; else the newest overall.
                        for (let i = ring.length - 1; i >= 0; i--) {
                            const r = ring[i];
                            if (endCycle && r.cycle_id === endCycle) { audioOutcome = r; break; }
                        }
                        if (!audioOutcome) {
                            for (let i = ring.length - 1; i >= 0; i--) {
                                if (ring[i].channel === chapter) { audioOutcome = ring[i]; break; }
                            }
                        }
                        if (!audioOutcome) audioOutcome = ring[ring.length - 1];
                    }
                } catch (_) { /* ring read is best-effort */ }
                _dispatch('narration_end', `ch${chapter}-end`, {
                    channel: chapter,
                    narration_idx: _narrationIdx,
                    // [combat-validation-observability 3b] cycle_id on the end
                    // frame too (playback_complete carries cycle_id).
                    audit_key: endCycle,
                    // [voice-drop-observability] actual outcome vs the assumed
                    // text-length timing (null if no audio outcome recorded).
                    audio_outcome: audioOutcome ? audioOutcome.outcome : null,
                    audio_outcome_detail: audioOutcome || null,
                });
                if (_midTimer) {
                    clearTimeout(_midTimer);
                    _midTimer = null;
                }
                _activeChapter = null;
                break;
            }
            default:
                break;
        }
    }

    // Observe incoming WebSocket messages by wrapping handleGameServerMessage.
    // Errors are handled inline in the central switch (no dedicated method),
    // so wrapping the intake is the stable hook point. TurnPump events cover
    // the rest via _observePumpEvent.
    // [W1 P2 combat_log retirement, 2026-07-09] The combat_log extraction
    // branch was removed with the legacy wire vector; the tap now only
    // captures gandalf_error frames.
    function _wireIntakeTap() {
        try {
            if (typeof window.GameServerConnection !== 'undefined' &&
                typeof window.GameServerConnection.handleGameServerMessage === 'function' &&
                !window.GameServerConnection.__vpWrappedIntake) {
                const _orig = window.GameServerConnection.handleGameServerMessage.bind(
                    window.GameServerConnection,
                );
                window.GameServerConnection.handleGameServerMessage = function (message) {
                    try {
                        if (message && message.type === 'gandalf_error') {
                            _dispatch('error', 'error-gandalf', {
                                message: String(message.message || '').substring(0, 200),
                            });
                        }
                        // [2026-07-27-002-QASTI Tier 3] The two pre-gameplay UX
                        // moments worth a picture. Both are observed here rather
                        // than approximated onto a nearby gameplay event: the
                        // `connected` frame IS world-join completion (the WS is
                        // up and the world is allocated), and the
                        // `character_choice` step IS the roster presentation
                        // (the portrait grid the player picks from).
                        if (message && message.type === 'connected') {
                            _dispatch('world_join', 'world-join', {
                                gameserver_name: message.gameserver_name || null,
                                needs_creation: !!message.needs_creation,
                            });
                        }
                        if (message && message.type === 'creation_flow'
                            && message.step === 'character_choice') {
                            _dispatch('character_select', 'character-select', {
                                offered: Array.isArray(message.characters)
                                    ? message.characters.length : 0,
                            });
                        }
                    } catch (_) { /* ignore */ }
                    return _orig(message);
                };
                window.GameServerConnection.__vpWrappedIntake = true;
            }
        } catch (_) { /* ignore */ }
    }

    // --- Slug helpers -----------------------------------------------------
    function _slugCommand(cmd) {
        if (!cmd || typeof cmd !== 'string') return 'cmd';
        const slug = cmd
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, 20);
        return slug || 'cmd';
    }

    // Expose on window for in-browser debugging and for the style-guide preview
    // to exercise the probe manually if desired.
    window.__vpProbe = {
        slug: _slugCommand,
        dispatch: _dispatch,
        label: () => window.__VP_PROBE_LABEL,
    };

    // --- End-of-session -----------------------------------------------------
    // A human session has NO teardown hook -- the player closes the tab. The
    // server therefore regenerates screens.md from the durable events.jsonl on
    // every frame (so the capture dir is survey-ingestable at any moment), and
    // `final` just tells it to flush the bundle to GCS now rather than on the
    // next multiple-of-N. We cannot reliably complete an html2canvas during
    // unload, so we do NOT try to force a last frame -- we only stamp the ones
    // still in flight.
    function _wireSessionEnd() {
        const mark = () => { _sessionEnding = true; };
        try {
            window.addEventListener('pagehide', mark);
            window.addEventListener('beforeunload', mark);
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') mark();
            });
        } catch (_) { /* best-effort */ }
    }

    // --- Boot -------------------------------------------------------------
    function _boot() {
        _wireHooks();
        _wireIntakeTap();
        _wireSessionEnd();
        _renderBadge();
        // Retry wiring once scripts are loaded (deferred may fire before all
        // globals exist).
        setTimeout(() => { _wireHooks(); _wireIntakeTap(); }, 250);
        setTimeout(() => { _wireHooks(); _wireIntakeTap(); }, 1000);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', _boot);
    } else {
        _boot();
    }
})();
